import { DOCUMENT } from '@angular/common';
import { DestroyRef, Directive, ElementRef, NgZone, Optional, inject } from '@angular/core';
import { NgSelectComponent } from '@ng-select/ng-select';

/**
 * Shows the FULL text of an ng-select option / selected value as a native
 * tooltip on hover.
 *
 * Why this is needed: the wizard's dropdowns sit in narrow side panels, so
 * long dataset / column names get clipped with an ellipsis both in the open
 * panel (`.ng-option-label`) and in the selected chips (`.ng-value-label`).
 * Widening the panel would bleed into the canvas, so a `title` tooltip is the
 * least intrusive way to let the user read the whole name.
 *
 * How it works: each `.ss-select` panel is `appendTo="body"`, so the options
 * are NOT inside the host element. While this select is open, a single
 * delegated `mouseover` listener on the document picks up the hovered
 * `.ng-option` and sets its `title` from its own text — lazily, so it also
 * covers rows that ng-select re-renders on scroll or search. ng-select ids
 * every option `${dropdownId}-${index}`, which scopes the listener to THIS
 * select's panel. The selected values live inside the host, so a listener on
 * the host handles those.
 *
 * Both listeners are registered OUTSIDE Angular's zone: setting a `title`
 * attribute needs no change detection, and an extra change-detection pass per
 * hover would make ng-select re-render its option list (its `[items]` input
 * is rebuilt by several step templates), replacing the node under the cursor
 * mid-click.
 *
 * Applied automatically to EVERY ng-select of a component that imports it:
 * the wizard's `.ss-select` pickers, the dashboard filter dropdowns
 * (GlobalDropdownComponent) and the chat's pickers alike.
 */
@Directive({
  selector: 'ng-select',
  standalone: true,
})
export class SsSelectTooltipDirective {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly document = inject(DOCUMENT);
  private readonly zone = inject(NgZone);
  private listening = false;

  private readonly onPanelHover = (event: Event): void => {
    const option = closestElement(event.target, '.ng-option');
    if (!option || !this.ngSelect || !option.id.startsWith(`${this.ngSelect.dropdownId}-`)) {
      return;
    }
    applyTitle(option, option.querySelector('.ng-option-label') ?? option);
  };

  private readonly onValueHover = (event: Event): void => {
    const value = closestElement(event.target, '.ng-value');
    if (!value || !this.host.nativeElement.contains(value)) {
      return;
    }
    // Read the label span, not the chip itself: a multi-select chip also
    // contains the "×" remove icon.
    applyTitle(value, value.querySelector('.ng-value-label') ?? value);
  };

  // The ng-select instance this directive sits on. Optional guards against the
  // selector ever matching a non-ng-select element.
  constructor(@Optional() private readonly ngSelect: NgSelectComponent | null) {
    if (!ngSelect) {
      return;
    }
    const element = this.host.nativeElement;
    this.zone.runOutsideAngular(() => element.addEventListener('mouseover', this.onValueHover));
    const opened = ngSelect.openEvent.subscribe(() => this.listenToPanel(true));
    const closed = ngSelect.closeEvent.subscribe(() => this.listenToPanel(false));
    inject(DestroyRef).onDestroy(() => {
      opened.unsubscribe();
      closed.unsubscribe();
      element.removeEventListener('mouseover', this.onValueHover);
      this.listenToPanel(false);
    });
  }

  private listenToPanel(on: boolean): void {
    if (on === this.listening) {
      return;
    }
    this.listening = on;
    if (on) {
      this.zone.runOutsideAngular(() => this.document.addEventListener('mouseover', this.onPanelHover, true));
    } else {
      this.document.removeEventListener('mouseover', this.onPanelHover, true);
    }
  }
}

function closestElement(target: EventTarget | null, selector: string): HTMLElement | null {
  return target instanceof Element ? (target.closest(selector) as HTMLElement | null) : null;
}

function applyTitle(target: HTMLElement, source: Element): void {
  const text = (source.textContent ?? '').replace(/\s+/g, ' ').trim();
  if (text && target.title !== text) {
    target.title = text;
  }
}
